import React, { useEffect, useRef, useState } from "react";
import { HelpCircle, X } from "lucide-react";
import { useFeatureFlag, FLAGS } from "../../config/featureFlags";

/**
 * AgenticGlossary — "What do these words mean?" for Settings (task #36,
 * Brad: "helpful notes in the settings ui like ? then you click and
 * What's an MCP etc and the agentic glossary is there").
 *
 * Every entry: plain-English definition + a "Why you care" line. Written
 * for business operators, not engineers — no jargon explains jargon.
 */
export const GLOSSARY = [
  {
    id: "agent",
    term: "Agent",
    what: "An AI that doesn't just answer — it does. It reads your ask, picks the right tools (email, CRM, calendar, files), runs them, and reports back with receipts.",
    why: "You stop being the operator of ten apps and start being the boss of one crew.",
  },
  {
    id: "mcp",
    term: "MCP",
    what: "Model Context Protocol — the standard plug that connects an AI to a real service (Gmail, HubSpot, GHL, Drive...). One MCP = one service your agent can actually operate, not just talk about.",
    why: "Every MCP you connect turns \"paste it in and I'll look\" into \"done, here's the result.\"",
  },
  {
    id: "custom-mcp",
    term: "Custom MCP",
    what: "A connector we build on the spot for a service we don't ship out of the box. Bring the API key from any tool with an API and your agent builds its own tools for it — takes minutes, works forever after.",
    why: "No more \"we don't integrate with that.\" If it has an API, it's yours.",
  },
  {
    id: "skill",
    term: "Skill",
    what: "A saved playbook your agents run on demand — a landing page builder, an offer audit, a Friday client recap. Built-in ones ship with the factory; yours you build with the guided wizard (Settings → Skills → Build a skill) or by telling Ollie \"make this a skill.\" Run any of them by typing /its-name in chat or Telegram.",
    why: "Your best process stops living in your head and starts running without you — the same way every time, on every surface.",
  },
  {
    id: "slash-command",
    term: "/slash command",
    what: "Type / at the start of a message and pick a skill — /offer-architect, /friday-recap — and your agent runs that playbook for this turn. Your own skills show first with a ★.",
    why: "One word replaces the paragraph of instructions you used to retype.",
  },
  {
    id: "run-notes",
    term: "Run notes (\"What recent runs taught us\")",
    what: "After a skill runs, your agent logs what worked, what didn't, and may propose one rule for next time. Nothing changes until you say yes — accept it in chat, or decline it on the skill page and it's never proposed again.",
    why: "Skills are living documents. They get sharper with every run instead of going stale.",
  },
  {
    id: "team-sharing",
    term: "Team sharing (Yours / Team / Built-in)",
    what: "Share one of your skills read-only with your team from its page. Teammates see it under \"Shared with your team,\" can run it, and can \"Make it mine\" to get their own editable copy. Only you can edit or delete the original.",
    why: "Your playbooks become the team's playbooks without anyone rewriting them — and without anyone breaking yours.",
  },
  {
    id: "automation",
    term: "Automation",
    what: "A skill or task on a schedule or a trigger. \"Every Monday 9am, pull new leads and draft outreach\" — it fires whether you're at the desk or at the gym.",
    why: "This is the \"less human doing\" part. Work happens between your sessions, not just during them.",
  },
  {
    id: "custom",
    term: "Custom (the 🛠️ button)",
    what: "Everything you've built for yourself, in one place: Dashboards, Skills, Connections, and Custom tools. Hover the Custom button in the header, or open this tab — same list, both directions.",
    why: "Fewer buttons up top, and nothing you made is ever more than one hover away.",
  },
  {
    id: "custom-dashboard",
    term: "Custom dashboard",
    what: "Widgets and reports built from your own data — ad spend, funnel numbers, portal engagement — with a guided wizard or by asking Ollie. Share one read-only with your team.",
    why: "The numbers you check every morning, on one screen, without logging into five tools.",
  },
  {
    id: "connections",
    term: "Connections (Integrations)",
    what: "The accounts your agents are allowed to act on — Google, GitHub, Canva, your CRM. Connect once under Integrations; every agent (Ollie, Mako, Orca, Harry) can then use its tools. Integrations is the room; connections and tools are what's in it.",
    why: "\"Connected\" is the difference between advice and action.",
  },
  {
    id: "built-in-connections",
    term: "Built-in connections (formerly Superpowers)",
    what: "Connections we already built to get you started — paste a key or click Connect and the matching tools switch on. The limit: a connection only works as far as your key's scopes reach, so with a limited key some of its tools won't work. Want a custom fit? Build a Custom MCP — it form-fits to your exact key — and ask Ollie if you need help.",
    why: "You know exactly what a built-in can and can't do before you count on it, and there's always a custom path when it can't.",
  },
  {
    id: "coach",
    term: "Coach (ask the coach)",
    what: "When you bring a business-growth situation — an offer that isn't converting, a close rate, pricing, lead flow, feeling stuck — Ollie first consults the founder's coaching layer: his frameworks, curriculum, and real mastermind coaching calls. Then it answers diagnosis-first: one bottleneck, one framework, one experiment for this week.",
    why: "You get the coaching, not a generic chatbot pep talk — and it names the ONE thing to fix.",
  },
  {
    id: "receipts",
    term: "🧾 Receipts",
    what: "The line under a reply that lists exactly which tools actually ran, with their real ids — verified by the platform, not claimed by the model. If nothing ran, it says so.",
    why: "\"Done ✅\" only counts when the receipt agrees. You never have to wonder whether the email really went out.",
  },
  {
    id: "byok",
    term: "BYOK / Connect your Claude",
    what: "Bring Your Own Key — you connect YOUR Claude account (one click, OAuth) and your AI usage runs on your own subscription instead of through ours.",
    why: "No middleman markup on your AI usage, and your quota is yours alone.",
  },
  {
    id: "oauth",
    term: "OAuth vs API key",
    what: "Two ways to connect a service. OAuth = click, log in, approve — the service hands us a scoped pass. API key = you copy a secret string from their settings page and paste it here. Same result: your agent can operate that service.",
    why: "OAuth is safer and revokable from the provider's side; API keys are the fallback when a service doesn't offer OAuth.",
  },
  {
    id: "fort-knox",
    term: "Fort Knox",
    what: "Where your keys and tokens live — encrypted at rest, scoped to your account only, never in chat logs, never shared across users.",
    why: "You can hand your agent real credentials without wondering where they ended up.",
  },
  {
    id: "shared-brain",
    term: "Shared brain (Ollie / Mako / Orca)",
    what: "One memory, many doors. Ollie is this web app, Mako is your Telegram line, Orca lives on your desktop. Same conversations, same files, same tools — start a thread on your phone, finish it here.",
    why: "No siloed sessions. The context you built up follows you to whatever surface you're on.",
  },
  {
    id: "harry",
    term: "Harry",
    what: "The heavy — a second, stronger AI pass that steps in when a job fails or the first answer doesn't hold up against the evidence.",
    why: "Hard problems get a second set of (bigger) eyes automatically. You don't have to ask twice.",
  },
  {
    id: "device-key",
    term: "Device key",
    what: "A per-device credential that lets a surface like Orca or a bot talk to your account without your password. Each one is named, scoped, and revokable on its own.",
    why: "Lose a laptop? Revoke one key. Everything else keeps working.",
  },
  {
    id: "github-backup",
    term: "GitHub Backup",
    what: "A daily snapshot of your skills, dashboards, and workspace files pushed to a private repo YOU own. Credentials are stripped before every sync. One-way out — nothing touches your other repos.",
    why: "Your business logic is portable property, not something trapped in our platform.",
  },
  {
    id: "tokens",
    term: "Tokens / usage",
    what: "How AI work is metered — roughly chunks of words in and out. Big documents and long conversations use more; quick asks use less.",
    why: "It's why the Usage tab exists: you can see exactly where your AI spend goes, task by task.",
  },
];

/**
 * HelpDot — the small "?" affordance. Self-contained: renders the chip and,
 * when clicked, the glossary panel (optionally scrolled to `term`).
 */
export const HelpDot = ({ term = null, label = "What do these words mean?" }) => {
  const enabled = useFeatureFlag(FLAGS.SETTINGS_GLOSSARY);
  const [open, setOpen] = useState(false);
  if (!enabled) return null;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={label}
        title={label}
        style={{
          background: "transparent",
          border: "none",
          padding: 2,
          marginLeft: 6,
          cursor: "pointer",
          color: "var(--text-secondary)",
          display: "inline-flex",
          verticalAlign: "middle",
        }}
      >
        <HelpCircle size={15} />
      </button>
      {open && <GlossaryPanel focusTerm={term} onClose={() => setOpen(false)} />}
    </>
  );
};

const GlossaryPanel = ({ focusTerm, onClose }) => {
  const focusRef = useRef(null);
  useEffect(() => {
    focusRef.current?.scrollIntoView({ block: "start" });
  }, []);
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1200,
        background: "rgba(0,0,0,.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Agentic glossary"
        style={{
          background: "var(--bg-secondary)",
          border: "1px solid var(--border-primary)",
          borderRadius: 14,
          width: "min(680px, 94vw)",
          maxHeight: "82vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "14px 18px",
            borderBottom: "1px solid var(--border-primary)",
          }}
        >
          <div>
            <h3 style={{ margin: 0 }}>The Agentic Glossary</h3>
            <p style={{ margin: "2px 0 0", fontSize: 13, color: "var(--text-secondary)" }}>
              Plain English for every word on this screen — and why it matters to your business.
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close glossary"
            style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--text-secondary)" }}
          >
            <X size={18} />
          </button>
        </div>
        <div style={{ overflowY: "auto", padding: "6px 18px 18px" }}>
          {GLOSSARY.map((g) => (
            <div
              key={g.id}
              ref={g.id === focusTerm ? focusRef : null}
              style={{
                padding: "12px 0",
                borderBottom: "1px solid var(--border-secondary)",
                background: g.id === focusTerm ? "var(--bg-accent-subtle)" : "transparent",
                borderRadius: g.id === focusTerm ? 8 : 0,
                paddingLeft: g.id === focusTerm ? 10 : 0,
                paddingRight: g.id === focusTerm ? 10 : 0,
              }}
            >
              <strong style={{ fontSize: 14.5 }}>{g.term}</strong>
              <p style={{ margin: "4px 0 0", fontSize: 13.5, lineHeight: 1.55 }}>{g.what}</p>
              <p style={{ margin: "4px 0 0", fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.5 }}>
                <em>Why you care:</em> {g.why}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default HelpDot;
